<template>
	<div
		class="w-100 bg-white font-weight-normal"
		:style="colorVars"
		style="padding-top: 110px;">
		<hero-section
			:title="heroSectionData.title"
			:subtitle="heroSectionData.subtitle"
			:description="heroSectionData.description"
			:images="heroSectionData.images"
			:watch-video-link="heroSectionData.watchVideoLink"
			@buyNowClicked="goTo('buy-now')" />
		<tagline-banner :text="taglineBannerData.text" />
		<routine-timeline />
		<disclaimer-section />
		<buy-now-section
			:product-image="buyNowData.productImage"
			:product-name="buyNowData.productName"
			:product-subtitle="buyNowData.productSubtitle"
			:price="buyNowData.price"
			:product-decoded-name="buyNowData.productDecodedName"
			:product-sku="buyNowData.productSku"
			:product-velovita-sku="buyNowData.productVelovitaSku"
			:product-jns-sku="buyNowData.productJnsSku"
			:variants="buyNowData.variants" />
		<contact-banner />
	</div>
</template>

<script>
import { PRODUCT_PAGES } from '@/settings/JnsLandingPages';
import Page from '@/views/JnsLandingPages/mixins/Page';
import Storage from '@/views/JnsLandingPages/mixins/Storage';
import Colors from '@/views/JnsLandingPages/mixins/Colors';
import PublicStore from '@/mixins/PublicStore';
import HeroSection from '@/views/JnsLandingPages/components/HeroSection.vue';
import TaglineBanner from '@/views/JnsLandingPages/components/TaglineBanner.vue';
import ContactBanner from '@/views/JnsLandingPages/components/ContactBanner.vue';
import BuyNowSection from '@/views/JnsLandingPages/components/BuyNowSection.vue';
import Replacements from '@/views/JnsLandingPages/mixins/Replacements';
import { JnsLandingPages } from '@/translations';
import RoutineTimeline from '@/views/JnsLandingPages/components/LandingPageTemplate3/RoutineTimeline.vue';
import Scroll from '@/views/JnsLandingPages/mixins/Scroll';
import DisclaimerSection from '@/views/JnsLandingPages/components/DisclaimerSection.vue';
import ReplicatedSite from '@/util/ReplicatedSite';
import { RESTRICTED_BY_COUNTRY } from '@/settings/LandingPages2';

export default {
	name: 'LandingPageTemplate3',
	messages: [JnsLandingPages],
	components: {
		DisclaimerSection,
		HeroSection,
		TaglineBanner,
		ContactBanner,
		BuyNowSection,
		RoutineTimeline,
	},
	mixins: [Page, Storage, Replacements, Colors, PublicStore, Scroll],
	data() {
		return {
			replicated: new ReplicatedSite(),
		};
	},
	computed: {
		heroSectionData() {
			const {
				title, subtitle, description, images, watchVideoLink,
			} = PRODUCT_PAGES[this.pageName].hero;

			return {
				title: this.applyReplacements(this.translate(title)),
				subtitle: this.applyReplacements(this.translate(subtitle)),
				description: this.applyReplacements(this.translate(description)),
				images: images.map((image) => this.requireAsset(image)),
				watchVideoLink,
			};
		},
		taglineBannerData() {
			const { tagline } = PRODUCT_PAGES[this.pageName];

			if (!tagline) {
				return { text: '' };
			}

			return {
				text: this.applyReplacements(this.translate(tagline.text)),
			};
		},
		contactBannerData() {
			const { contactBanner } = PRODUCT_PAGES[this.pageName];

			if (!contactBanner) {
				return { text: '', leftIcon: '', rightIcon: '' };
			}

			return {
				text: this.applyReplacements(this.translate(contactBanner.text)),
				leftIcon: this.requireAsset(contactBanner.leftIcon),
				rightIcon: this.requireAsset(contactBanner.rightIcon),
			};
		},
		buyNowData() {
			const { buyNow, hero } = PRODUCT_PAGES[this.pageName];

			if (!buyNow) {
				return {};
			}

			return {
				productImage: buyNow.productImage,
				productName: this.applyReplacements(this.translate(buyNow.productName || hero.title)),
				productSubtitle: this.applyReplacements(this.translate(buyNow.productSubtitle || hero.subtitle)),
				price: this.applyReplacements(this.translate(buyNow.price)),
				productDecodedName: buyNow.productDecodedName || buyNow.product_decoded_name || '',
				productSku: buyNow.productSku || '',
				productVelovitaSku: buyNow.productVelovitaSku || buyNow.product_velovita_sku || '',
				productJnsSku: buyNow.productJnsSku || buyNow.product_jns_sku || '',
				variants: (buyNow.variants || []).map((v) => ({
					code: v.code,
					sku: v.sku,
					velovitaSku: v.velovitaSku || v.velovita_sku,
					jnsSku: v.jnsSku || v.jns_sku,
					decodedName: v.decodedName || v.decoded_name,
					title: this.applyReplacements(this.translate(v.title || '')),
					image: v.image,
				})),
			};
		},
	},
	created() {
		// Match gating logic from older landing pages (e.g., /landing-pages/bran)
		if (!this.showPublicStore) {
			this.$router.replace({ name: 'Store' }).catch(() => {});
		}

		const restricted = RESTRICTED_BY_COUNTRY[this.country] ?? RESTRICTED_BY_COUNTRY.default ?? [];
		if (restricted.includes(this.pageName)) {
			this.$router.replace({ name: 'Store' });
		}

		this.replicated.clear();

		const replicatedUsername = this.$replicated.replicatedId();
		if (replicatedUsername) {
			this.replicated.getReplicatedSiteNav(replicatedUsername).catch(() => {
				this.$replicated.siteOff();
				this.$replicated.siteReplicatedSponsorOff();
				this.$replicated.siteReplicatedPageOff();
			});
		}
	},
};
</script>

<style>
.jlp-primary-color-bold {
	color: var(--jlp-primary-color);
	font-weight: bold;
}
</style>
